import { Component, OnInit } from '@angular/core';
import { BentoFileSelectorEvent, BentoAlertItemOptions } from '@bento/bento-ng';
import { Observable, of, } from 'rxjs';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { getBindings } from '@onviobr/services/session';
import { getRoute } from '@onviobr/services/environment';
import { has } from '@onviobr/services/permission';
import { has as hasAccess } from '@onviobr/services/access';
import { IThemeV1 } from '@onviobr/services/profile';
import { getTheme, updateTheme } from '@onviobr/services/profile/themes';
import { DomSanitizer } from '@angular/platform-browser';
import { SiteSettingsService } from './site-settings.service';

function isDefined(val: any) {
	return !(val === undefined || val == null);
}
export class RGB {
	private r: number;
	private g: number;
	private b: number;
	private hexString: string;

	constructor(_hexString: string) {
		const _r = _hexString.substring(1, 3);
		const _g = _hexString.substring(3, 5);
		const _b = _hexString.substring(5, 7);
		this.hexString = _hexString;
		this.r = parseInt(_r, 16);
		this.g = parseInt(_g, 16);
		this.b = parseInt(_b, 16);
	}

	getRGBString(): Observable<string> {
		return of(`rgb(${this.r}, ${this.g}, ${this.b})`);
	}

	getRGBStringWithAlpha(alpha: number): Observable<string> {
		return of(`rgba(${this.r}, ${this.g}, ${this.b}, ${alpha ? alpha : 1})`);
	}

	getHexString(): string {
		return this.hexString;
	}
}

const commonStyle = (scope: string, accentColor?: string, buttonFontColor?: string): string => `
		.onvio${scope}-sign-in {
				display: flex; align-items: center; justify-content: center; flex-direction: column;
				width: 300px; height: 184px;
				border-radius: 10px; border: solid 2px #d5d4d9;
				background-color: white;
		}

		.onvio${scope}-sign-in span {
				font-family: Arial; font-size: 24px; font-weight: bold; color: #212121; margin-bottom: 25px;
		}

		.onvio${scope}-sign-in a {
				width: 150px; height: 46px;
				border-radius: 5px; border: solid 1px ${accentColor || '#b2b2b8'};
				background-color: ${accentColor || '#f4f5f7'};
				text-align: center; vertical-align: middle; line-height: 46px;
				font-family: Arial; font-size: 14px; font-weight: bold; letter-spacing: 1.1px;
				text-align: center;
				display: flex;
				align-items: center;
				justify-content: center;
		}
		.onvio${scope}-sign-in a, .onvio${scope}-sign-in a:hover,
		.onvio${scope}-sign-in a:focus, .onvio${scope}-sign-in a:visited, .onvio${scope}-sign-in a:active {
				text-transform: uppercase;
				text-decoration: none;
				color: ${buttonFontColor || '#212121'};
		}

		.onvio${scope}-sign-in a:focus {
				outline: solid black 3px !important;
				outline-offset: 1px;
		}

		.onvio${scope}-sign-in a:hover {
				border: solid 1px ${accentColor || '#b2b2b8'}; background-color: ${accentColor || '#FFFFFF'};
		}
`;

const onvioSignInCode = (rootUrl: string, sigInText: string, appText: string) => `
<style>${commonStyle('')}</style>
<div class="onvio-sign-in">
		<span>${appText}</span>
		<a rel="external noopener noreferrer" href="${rootUrl}" target="_blank">${sigInText}</a>
</div>`;

const clientCenterSignInCode = (rootUrl: string, accentColor: string, companyId: string, sigInText: string, appText: string) => `
<style>${commonStyle('-client', accentColor, '#ffffff')}</style>
<div class="onvio-client-sign-in">
		<span>${appText}</span>
		<a class="onvio-client-sign-in-button" rel="external 2 noopener noreferrer"
			href="${rootUrl}/clientcenter/company/${companyId}" target="_blank">${sigInText}</a>
</div>`;

@Component({
	selector: 'app-site-settings',
	providers: [SiteSettingsService],
	templateUrl: './site-settings.component.html',
	styleUrls: ['./site-settings.component.css']
})
export class SiteSettingsComponent implements OnInit {
	backgrounds = {
		grass: false,
		tulips: false,
		wheat: false,
		weeds: false,
		blossoms: false,
		fall: false,
		winter: false,
		sunflowers: false,
		mountains: false,
		clouds: false,
	};

	accentColors = {
		'#0072a8': { color: new RGB('#0072a8'), selected: false, colorName: 'Blue' },
		'#08817d': { color: new RGB('#08817d'), selected: false, colorName: 'TurquoiseBlue' },
		'#006237': { color: new RGB('#006237'), selected: false, colorName: 'Green' },
		'#cc6600': { color: new RGB('#cc6600'), selected: false, colorName: 'Orange' },
		'#990000': { color: new RGB('#990000'), selected: false, colorName: 'Red' },
		'#660000': { color: new RGB('#660000'), selected: false, colorName: 'Brown' },
		'#660066': { color: new RGB('#660066'), selected: false, colorName: 'Purple' },
		'#990066': { color: new RGB('#990066'), selected: false, colorName: 'Pink' },
		'#330066': { color: new RGB('#330066'), selected: false, colorName: 'DarkPurple' },
		'#000099': { color: new RGB('#000099'), selected: false, colorName: 'DarkBlue' },
		'#000033': { color: new RGB('#000033'), selected: false, colorName: 'Black' },
	};

	alerts: BentoAlertItemOptions[] = [];
	isLoading = true;
	isSaving = false;
	hasSetupPermission = false;
	firmBanner: string;
	bannerExist = false;
	logoExist = false;
	headerExist = false;
	isAuth0Disabled = false;

	public documentId: string;
	public employeeId: string;
	objectKeys = Object.keys;
	modalInstance: NgbModalRef;

	headerIsText = 'HeaderText';

	selectedFiles: File[];
	imageSuccess: boolean;
	imageErrorStrings: string[];

	onvioSigninHTML: string;
	clientCenterSigninHTML: string;
	rootUrl = 'https://onvio.us';

	theme: IThemeV1;

	public getBindings = getBindings;
	public hasPermission = has;
	public hasBRHideStaffFirmLogoSetting = false;
	public access = hasAccess;

	public getTheme = getTheme;
	public updateTheme = updateTheme;
	public getRoute = getRoute;

	public companyId: string;
	public previousVal: {
		theme?: IThemeV1;
	};

	isDefined = isDefined;

	constructor(
		public settingsService: SiteSettingsService,
		public modalService: NgbModal,
		public translate: TranslateService,
		public sanitizer: DomSanitizer,
		public http: HttpClient,
	) {
		this.save = this.save.bind(this);
		window['saveSiteSettings'] = this.save;
	}

	async ngOnInit() {
		this.previousVal = {};
		try {
			const bindings = await this.getBindings();
			this.rootUrl = await this.getRoute('Root');
			this.companyId = bindings.ApplicationConfiguration.CompanyId;
			const promiseArray = [
				this.readTheme(await this.getTheme(this.companyId)),
				this.createSignInText(),
				this.getFirmThemes(),
			];
			await Promise.all(promiseArray);
			this.hasSetupPermission = await this.hasPermission('OnvioSetup.Firm');
			this.isLoading = false;
			this.isAuth0Disabled = await this.access('Platform_DisableAuth0Phase2');
			this.hasBRHideStaffFirmLogoSetting = await this.access('BRHideStaffFirmLogoSetting');
		} catch (error) {
			console.error(error);
		}
	}

	public async createSignInText() {
		const sigInText = await this.translate.get([
			'onvio.appearance.html.signIn', 'onvio.setup.appearance.signinForm.staff', 'onvio.setup.appearance.signinForm.client',
		]).toPromise();
		this.onvioSigninHTML = onvioSignInCode(
			this.rootUrl,
			sigInText['onvio.appearance.html.signIn'],
			sigInText['onvio.setup.appearance.signinForm.staff'],
		);
		this.clientCenterSigninHTML = clientCenterSignInCode(
			this.rootUrl,
			this.theme.colors.buttonFillColor,
			this.companyId,
			sigInText['onvio.appearance.html.signIn'],
			sigInText['onvio.setup.appearance.signinForm.client'],
		);
	}

	public async readTheme(theme: IThemeV1) {
		this.theme = theme;
		this.setPreviousTheme(theme);

		if (theme.headerImage) {
			this.imageSuccess = true;
			this.headerIsText = 'HeaderImage';
		} else if (theme.bannerImage) {
			this.headerIsText = 'HeaderLogo';
		} else if (this.isDefined(this.theme.headerLine1)) {
			this.imageSuccess = false;
			this.headerIsText = 'HeaderText';
		}

		this.selectBackground(theme.loginBackground);
		this.selectColor(theme.colors.buttonFillColor);
	}

	async createFirmLogoLink() {
		try {
			const response = await this.settingsService.getBannerThemeEntry(this.companyId);
			if (response && response.toString().substring(0, 10) === 'data:image') {
				this.firmBanner = response.toString();
			} else {
				this.firmBanner = this.rootUrl + '/api/profiles/v1/themes/banner?company=' + this.companyId;
			}

		} catch (e) {
			this.firmBanner = this.rootUrl + '/api/profiles/v1/themes/banner?company=' + this.companyId;
			this.translate.get('Bluemoon.Practice.Common.ErrorHandler.GenericValidationError').toPromise().then(result => {
				this.alerts.push({
					msg: result,
					timeout: 5000,
					type: 'danger',
					closeable: true
				});
			});

		}

	}

	async getFirmThemes() {
		try {
			const response = await this.settingsService.getFirmThemes(this.companyId);
			const bannerImageResult = await this.getBannerImageResult();
			const logoResult = await this.getLogoResult();
			if (response && response.hasOwnProperty('bannerImage')) {
				if (bannerImageResult && bannerImageResult.size !== 0) {
					this.bannerExist = true;
				} else {
					this.bannerExist = false;
					this.theme.bannerImage = null;
					if (this.theme.headerImage) {
						this.imageSuccess = true;
						this.headerIsText = 'HeaderImage';
					} else if (this.isDefined(this.theme.headerLine1) && !this.bannerExist) {
						this.imageSuccess = false;
						this.headerIsText = 'HeaderText';
					}
				}
			} else {
				this.bannerExist = false;
				this.theme.bannerImage = null;
			}
			if (response && response.hasOwnProperty('logoImage') && logoResult && logoResult.size !== 0) {
				this.logoExist = true;
			} else {
				this.logoExist = false;
				this.theme.logoImage = null;
			}
			if (response && response.hasOwnProperty('headerImage')) {
				this.headerExist = response.hasOwnProperty('headerImage');
			} else {
				this.headerExist = false;
			}
			this.populateImages(bannerImageResult, logoResult);
		} catch (error) {
			this.translate.get('Bluemoon.Practice.Common.ErrorHandler.GenericValidationError').toPromise().then(result => {
				this.alerts.push({
					msg: result,
					timeout: 5000,
					type: 'danger',
					closeable: true
				});
			});
		}
	}

	async getBannerImageResult(): Promise<Blob>	| undefined {
		try {
			return await this.settingsService.getBannerImage(this.companyId);
		} catch(error) {
			return undefined;
		}
	}

	async getLogoResult(): Promise<Blob>	| undefined {
		try {
			return await this.settingsService.getLogoImage(this.companyId);
		} catch(error) {
			return undefined;
		}
	}

	async populateImages(bannerImageResult: Blob, logoResult: Blob) {
		try {
			if (this.bannerExist) {
				this.createFirmLogoLink();
				if (bannerImageResult) {
					this.createImageFromBlob(bannerImageResult, 'bannerImage');
				}
			}
			if (this.logoExist) {
				if (logoResult) {
					this.createImageFromBlob(logoResult, 'logoImage');
				}
			}
			if (this.headerExist) {
				const headerImageResult = await this.settingsService.getHeaderImage(this.companyId);
				if (headerImageResult) {
					this.createImageFromBlob(headerImageResult, 'headerImage');
				}
			}
		} catch (error) {
			this.translate.get('Bluemoon.Practice.Common.ErrorHandler.GenericValidationError').toPromise().then(result => {
				this.alerts.push({
					msg: result,
					timeout: 5000,
					type: 'danger',
					closeable: true
				});
			});
		}
	}


	createImageFromBlob(image: Blob, imageFor: string) {
		const reader = new FileReader();
		reader.addEventListener('load', () => {
			if (imageFor === 'logoImage') {
				this.theme.logoImage = reader.result.toString();
			} else if (imageFor === 'bannerImage') {
				this.theme.bannerImage = reader.result.toString();
			} else if (imageFor === 'headerImage') {
				this.theme.headerImage = reader.result.toString();
			}
		}, false);
		if (image) {
			reader.readAsDataURL(image);
		}
	}

	getFirmClientLink(): string {
		return this.rootUrl + '/staff/#/setup/firm/general';
	}

	handleImage(image): any {
		this.imageErrorStrings = [];

		if (image.height > 80) {
			this.translate.get('Onvio.Setup.Theme.Image.Error.Height').toPromise().then(result => {
				this.imageErrorStrings.push(result);
			});
			this.removeImage();
			return;
		}
		if (image.width > 500) {
			this.translate.get('Onvio.Setup.Theme.Image.Error.Width').toPromise().then(result => {
				this.imageErrorStrings.push(result);
			});
			this.removeImage();
			return;
		}

		this.imageSuccess = true;
	}

	fileSelected(event: BentoFileSelectorEvent) {
		const filereader = new FileReader();
		/* istanbul ignore next */
		filereader.onload = () => {
			const image = new Image();

			this.theme.headerImage = filereader.result as string;
			image.src = this.theme.headerImage;

			image.onload = () => {
				image.src = this.theme.headerImage;
				image.onload = this.handleImage(image);
			};

		};
		filereader.readAsDataURL(event.files[0]);
	}


	async save() {
		this.isSaving = true;
		if (!this.imageSuccess) {
			this.theme.headerImage = null;
		}
		this.theme.headerLine1 = this.theme.headerLine1 || '';


		// Format the logout URL if it doesn't have http(s)://
		if (this.theme.logoutUrl) {
			this.theme.logoutUrl = this.getSignOutUrl();
		}

		try {
			await this.updateTheme(this.theme, this.companyId);
			this.translate.get('Onvio.Setup.Theme.Success').toPromise().then(result => {
				this.alerts.push({
					msg: result,
					timeout: 5000,
					type: 'success',
					closeable: true
				});
			});
		} catch (error) {
			this.translate.get('Bluemoon.Practice.Common.ErrorHandler.GenericValidationError').toPromise().then(result => {
				this.alerts.push({
					msg: result,
					timeout: 5000,
					type: 'danger',
					closeable: true
				});
			});
		}

		this.isSaving = false;
	}

	cancel() {
		this.readTheme(this.previousVal.theme);
	}

	removeImage() {
		this.imageSuccess = false;
		this.selectedFiles = [];
		this.theme.headerImage = null;
	}

	async selectColor(color: string) {
		for (const accent in this.accentColors) {
			/* istanbul ignore else */
			if (this.accentColors.hasOwnProperty(accent)) {
				this.accentColors[accent].selected = false;
			}
		}
		// If the color doesn't exist, create it
		if (!this.accentColors[color]) {
			this.accentColors[color] = { color: new RGB(color), selected: false, colorName: 'Default' };
		}

		this.accentColors[color].selected = true;
		this.theme.colors.buttonFillColor = color;

		await this.createSignInText();
	}

	launchSelector(content: any) {
		this.modalInstance = this.modalService.open(content, { container: '.ng-bento', size: 'lg' });
	}

	closeSelector(reason: string) {
		this.selectBackground(this.theme.loginBackground);
		this.modalInstance.close(reason);
	}

	selectBackground(background: string) {
		for (const bg in this.backgrounds) {
			if (this.backgrounds[bg]) {
				this.backgrounds[bg] = false;
				break;
			}
		}

		this.backgrounds[background] = true;
	}

	chooseBackground() {
		for (const background in this.backgrounds) {
			if (this.backgrounds[background]) {
				this.theme.loginBackground = background;
				break;
			}
		}
		this.modalInstance.close();
	}

	setPreviousValue(theme: IThemeV1) {
		this.setPreviousTheme(theme);
	}

	setPreviousTheme(theme: IThemeV1) {
		this.previousVal.theme = {
			...theme,
			colors: {
				...theme.colors
			}
		};
	}

	getSignOutUrl(): string {
		if (this.theme.logoutUrl) {
			if (this.theme.logoutUrl.startsWith('http')) {
				return this.theme.logoutUrl;
			} else {
				return `https://${this.theme.logoutUrl}`;
			}
		} else {
			return this.rootUrl + '/clientcenter';
		}
	}

	copyToClipboard(code: string) {
		/* istanbul ignore else */
		if (document.queryCommandSupported && document.queryCommandSupported('copy')) {
			const textarea = document.createElement('textarea');
			textarea.textContent = code;
			textarea.style.position = 'fixed';
			document.body.appendChild(textarea);
			textarea.select();
			try {
				return document.execCommand('copy');
			} finally {
				document.body.removeChild(textarea);
			}
		}
	}
}
